import { useMutation, useQuery } from "@tanstack/react-query";
import { Roles, useIdentity } from "context/auth";
import { useAuthenticatedRequest, useAuthenticatedRequestCreator } from "hooks/useRequests";
import CancelDeletion from "./CancelDeletion";
import DeleteAccount from "./DeleteAccount";

interface DeletePreFlightResult {
    ok: boolean;
    reasons: string[];
}

export const useAccountDeletion = (open: boolean) => {
    const { roles } = useIdentity();

    const preFlightCheck = useAuthenticatedRequest<DeletePreFlightResult>({
        method: "get",
        url: "/account/can-delete",
    });

    const enqueueDeletion = useAuthenticatedRequestCreator<void, void>(() => ({
        method: "delete",
        url: "/account"
    }), [400, 404]);

    const cancelDeletion = useAuthenticatedRequestCreator<void, void>(() => ({
        method: "post",
        url: "/account/cancel-delete"
    }), [400]);

    return {
        preFlightCheck: useQuery(["account", "can-delete"], preFlightCheck, { enabled: open }),
        enqueueDeletion: useMutation(enqueueDeletion),
        cancelDeletion: useMutation(cancelDeletion),
        isBreaker: roles.includes(Roles.Breaker)
    };
}

const AccountDeletion = ({
    deletionDate,
    onConfirm,
    shouldCancel = false
}: {
    deletionDate?: string | null,
    onConfirm: () => Promise<void>,
    shouldCancel?: boolean
}) => {
    if (!deletionDate) {
        return <DeleteAccount
            onConfirm={onConfirm}
        />
    }

    return <CancelDeletion
        deletionDate={deletionDate}
        onConfirm={onConfirm}
        immediately={shouldCancel!}
    />
}

export default AccountDeletion;